/**
 * Procedural textures (ART-BIBLE §3, §5). Everything here is generated in-browser —
 * no external image assets, so the whole kit stays on-palette by construction.
 *
 * "Grime is mandatory on every chassis. A clean cabinet reads as a render; a cabinet with
 *  30 years of cigarette film on the upper third reads as a place."
 */

import {
  CanvasTexture, RepeatWrapping, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace,
  NoColorSpace, type Texture,
} from 'three';
import { css, CREME, PAPIER, INK, NACHT } from './palette';

// ---------------------------------------------------------------- minification policy

/**
 * PRINTED THINGS MUST SURVIVE DISTANCE.
 *
 * Every label in this game is baked at 2× device scale (ART-BIBLE §5) and then seen from
 * the aisle at a tenth of that size. Without mipmaps a ~900×295 marquee minified to ~50 px
 * of screen samples near-random texels: the Ü loses its umlaut, the E loses its arms and
 * reads as L, the M reads as H. "MÜNZSCHIEBER" became "HUNTSCHIEBLU" — an aliasing bug that
 * looked exactly like a broken glyph atlas.
 *
 * So: trilinear minification + anisotropy on everything with type on it. WebGL2 mipmaps
 * non-power-of-two textures, so canvas sizes stay honest (no padding to 1024).
 * ART-BIBLE §9 tier 0 keeps the marquee visible at distance — visible means *legible*.
 */
let _maxAniso = 1;

/** every texture under the policy, so a late renderer can raise their anisotropy */
const _printed = new Set<Texture>();

/** called once by the stage: renderer.capabilities.getMaxAnisotropy() */
export function setMaxAnisotropy(n: number): void {
  const v = Math.max(1, Math.floor(n) || 1);
  if (v === _maxAniso) return;
  _maxAniso = v;
  for (const t of _printed) { t.anisotropy = v; t.needsUpdate = true; }
}

export function maxAnisotropy(): number { return _maxAniso; }

/**
 * Apply the printed-matter minification policy to a texture. Call it again after redrawing
 * the canvas — it sets `needsUpdate`, which is what regenerates the mip chain.
 */
export function printed<T extends Texture>(t: T): T {
  t.generateMipmaps = true;
  t.minFilter = LinearMipmapLinearFilter;   // trilinear: no shimmer between mip levels
  t.magFilter = LinearFilter;               // close up, the ink bleed stays soft, not blocky
  t.anisotropy = _maxAniso;
  t.needsUpdate = true;
  _printed.add(t);
  return t;
}

/** drop a disposed texture out of the policy set (nothing else holds it) */
export function unprinted(t: Texture): void { _printed.delete(t); }

// ---------------------------------------------------------------- rng / noise

export function rng(seed: number) {
  let s = (seed >>> 0) || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

function valueNoise(w: number, h: number, cells: number, seed: number): Float32Array {
  const r = rng(seed);
  const gw = cells + 1;
  const grid = new Float32Array(gw * gw);
  for (let i = 0; i < grid.length; i++) grid[i] = r();
  const out = new Float32Array(w * h);
  const sm = (t: number) => t * t * (3 - 2 * t);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const fx = (x / w) * cells, fy = (y / h) * cells;
      const x0 = Math.floor(fx), y0 = Math.floor(fy);
      const tx = sm(fx - x0), ty = sm(fy - y0);
      const g = (a: number, b: number) => grid[(b % gw) * gw + (a % gw)];
      const v = g(x0, y0) * (1 - tx) * (1 - ty) + g(x0 + 1, y0) * tx * (1 - ty)
        + g(x0, y0 + 1) * (1 - tx) * ty + g(x0 + 1, y0 + 1) * tx * ty;
      out[y * w + x] = v;
    }
  }
  return out;
}

function canvas(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  return { c, g };
}

const _cache = new Map<string, Texture>();
function cached(key: string, make: () => Texture): Texture {
  let t = _cache.get(key);
  if (!t) { t = make(); _cache.set(key, t); }
  return t;
}

// ---------------------------------------------------------------- grime

export interface GrimeOpts {
  seed?: number;
  /** 0..1 how much cigarette film collects at the top */
  topStain?: number;
  /** hand-worn bright rings (button areas) as [u,v,r] triples */
  wear?: [number, number, number][];
  size?: number;
}

/**
 * A luminance map, multiplied into the chassis colour. Dark = dirty.
 * Contains: fine grain, blotchy nicotine film weighted to the top, a few wear rings
 * (where hands polished the plastic clean), and one drip.
 */
export function grime(opts: GrimeOpts = {}): Texture {
  const key = 'grime|' + JSON.stringify(opts);
  return cached(key, () => {
    const S = opts.size ?? 256;
    const top = opts.topStain ?? 0.55;
    const { c, g } = canvas(S, S);
    const blot = valueNoise(S, S, 5, (opts.seed ?? 7) * 977 + 13);
    const fine = valueNoise(S, S, 34, (opts.seed ?? 7) * 131 + 7);
    const img = g.createImageData(S, S);
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const i = y * S + x;
        const vGrad = 1 - y / S;                        // 1 at top
        const stain = blot[i] * 0.55 + 0.45;
        let v = 1 - top * Math.pow(vGrad, 1.7) * (1 - stain * 0.55);
        v *= 0.93 + fine[i] * 0.09;                     // grain
        // one lazy drip from the top
        const dx = Math.abs(x - S * 0.72);
        if (dx < 3 && y < S * 0.45) v *= 0.82 + dx * 0.05;
        const o = i * 4;
        const b = Math.max(0, Math.min(255, Math.round(v * 255)));
        img.data[o] = b; img.data[o + 1] = b; img.data[o + 2] = b; img.data[o + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    // hands polished these spots clean
    for (const [u, v, r] of opts.wear ?? []) {
      const grd = g.createRadialGradient(u * S, (1 - v) * S, 0, u * S, (1 - v) * S, r * S);
      grd.addColorStop(0, 'rgba(255,255,255,0.55)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, S, S);
    }
    const t = new CanvasTexture(c);
    t.wrapS = t.wrapT = RepeatWrapping;
    t.colorSpace = NoColorSpace;
    t.anisotropy = 1;
    return t;
  });
}

// ---------------------------------------------------------------- halo / glow

/** radial falloff sprite for neon halos and light pools (additive) */
export function halo(sharpness = 2.4, size = 128): Texture {
  return cached(`halo|${sharpness}|${size}`, () => {
    const { c, g } = canvas(size, size);
    const img = g.createImageData(size, size);
    const R = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const d = Math.hypot(x - R + 0.5, y - R + 0.5) / R;
        const v = Math.max(0, 1 - d);
        const a = Math.pow(v, sharpness);
        const o = (y * size + x) * 4;
        img.data[o] = 255; img.data[o + 1] = 255; img.data[o + 2] = 255;
        img.data[o + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const t = new CanvasTexture(c);
    t.colorSpace = NoColorSpace;
    return t;
  });
}

/** soft elliptical pool for the light a sign throws on the floor */
export function pool(size = 128): Texture {
  return cached(`pool|${size}`, () => {
    const { c, g } = canvas(size, size);
    const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grd.addColorStop(0, 'rgba(255,255,255,0.9)');
    grd.addColorStop(0.45, 'rgba(255,255,255,0.35)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, size, size);
    const t = new CanvasTexture(c);
    t.colorSpace = NoColorSpace;
    return t;
  });
}

/** a single spark / dust mote */
export function spark(size = 64): Texture {
  return cached(`spark|${size}`, () => {
    const { c, g } = canvas(size, size);
    const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.25, 'rgba(255,255,255,0.65)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, size, size);
    const t = new CanvasTexture(c);
    t.colorSpace = NoColorSpace;
    return t;
  });
}

// ---------------------------------------------------------------- typography

export const FONT_NEON = '"Monoton"';
export const FONT_SIGN = '"Barlow Condensed"';
export const FONT_MONO = '"Share Tech Mono"';

export interface LabelOpts {
  font?: string;
  weight?: string;
  size?: number;          // px in the canvas
  color?: number;
  bg?: number | null;
  align?: CanvasTextAlign;
  padding?: number;
  width?: number;
  height?: number;
  letterSpacing?: string;
  lineHeight?: number;
  /** 1px ink bleed so text looks printed, not composited */
  bleed?: boolean;
  rotate?: number;
}

/** bake a line (or lines) of text into a texture — the only way text enters the 3D scene */
export function label(text: string | string[], opts: LabelOpts = {}): Texture {
  const lines = Array.isArray(text) ? text : [text];
  const size = opts.size ?? 64;
  const pad = opts.padding ?? size * 0.35;
  const font = `${opts.weight ?? ''} ${size}px ${opts.font ?? FONT_SIGN}`.trim();
  const lh = opts.lineHeight ?? size * 1.18;

  const probe = canvas(8, 8).g;
  probe.font = font;
  if (opts.letterSpacing) (probe as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
  const textW = Math.max(...lines.map((l) => probe.measureText(l).width));

  const W = Math.max(4, Math.ceil(opts.width ?? textW + pad * 2));
  const H = Math.max(4, Math.ceil(opts.height ?? lh * lines.length + pad * 2));
  const { c, g } = canvas(W, H);

  if (opts.bg !== null && opts.bg !== undefined) {
    g.fillStyle = css(opts.bg);
    g.fillRect(0, 0, W, H);
  }
  g.font = font;
  if (opts.letterSpacing) (g as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
  g.textBaseline = 'middle';
  g.textAlign = opts.align ?? 'center';
  const x = opts.align === 'left' ? pad : opts.align === 'right' ? W - pad : W / 2;

  lines.forEach((line, i) => {
    const y = pad + lh * (i + 0.5);
    if (opts.bleed !== false) {
      g.fillStyle = css(opts.color ?? INK, 0.28);
      g.fillText(line, x + 1, y + 1);
    }
    g.fillStyle = css(opts.color ?? INK);
    g.fillText(line, x, y);
  });

  const t = new CanvasTexture(c);
  t.colorSpace = SRGBColorSpace;
  return printed(t);
}

/** an alpha-only glyph mask, for neon tubes (the glyph IS the light) */
export function glyphMask(text: string, opts: LabelOpts = {}): { tex: Texture; w: number; h: number } {
  const size = opts.size ?? 128;
  const font = `${size}px ${opts.font ?? FONT_NEON}`;
  const probe = canvas(8, 8).g;
  probe.font = font;
  if (opts.letterSpacing) (probe as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
  const pad = size * 0.45;
  const W = Math.ceil(probe.measureText(text).width + pad * 2);
  const H = Math.ceil(size * 1.7);
  const { c, g } = canvas(W, H);
  g.font = font;
  if (opts.letterSpacing) (g as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = '#ffffff';
  g.fillText(text, W / 2, H / 2);
  const t = new CanvasTexture(c);
  t.colorSpace = NoColorSpace;
  return { tex: printed(t), w: W, h: H };
}

// ---------------------------------------------------------------- surfaces

/** cream wall tiles with grouting and a nicotine wash */
export function wallTiles(seed = 3): Texture {
  return cached(`wall|${seed}`, () => {
    const S = 256;
    const { c, g } = canvas(S, S);
    g.fillStyle = css(CREME.base);
    g.fillRect(0, 0, S, S);
    const n = 8, cell = S / n;
    const r = rng(seed);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const v = 0.88 + r() * 0.2;
        g.fillStyle = css(CREME.base, 1);
        g.globalAlpha = 1;
        g.fillStyle = `rgba(${Math.round(226 * v)},${Math.round(205 * v)},${Math.round(164 * v)},1)`;
        g.fillRect(x * cell + 1, y * cell + 1, cell - 2, cell - 2);
      }
    }
    g.globalAlpha = 0.5;
    g.strokeStyle = css(CREME.shade);
    g.lineWidth = 2;
    for (let i = 0; i <= n; i++) {
      g.beginPath(); g.moveTo(i * cell, 0); g.lineTo(i * cell, S); g.stroke();
      g.beginPath(); g.moveTo(0, i * cell); g.lineTo(S, i * cell); g.stroke();
    }
    g.globalAlpha = 1;
    const t = new CanvasTexture(c);
    t.wrapS = t.wrapT = RepeatWrapping;
    t.colorSpace = SRGBColorSpace;
    return t;
  });
}

/** worn linoleum: speckled 70s floor, the arcade's real signature surface */
export function linoleum(seed = 11): Texture {
  return cached(`lino|${seed}`, () => {
    const S = 256;
    const { c, g } = canvas(S, S);
    const base = valueNoise(S, S, 18, seed);
    const blot = valueNoise(S, S, 4, seed + 91);
    const img = g.createImageData(S, S);
    for (let i = 0; i < S * S; i++) {
      const speck = base[i] > 0.72 ? 1 : base[i] < 0.24 ? -1 : 0;
      const v = 0.80 + blot[i] * 0.16 + speck * 0.11;
      const o = i * 4;
      // worn 70s lino: warm grey-brown, faintly green-flecked. Never blue.
      img.data[o] = Math.round(Math.min(255, 134 * v));
      img.data[o + 1] = Math.round(Math.min(255, 120 * v));
      img.data[o + 2] = Math.round(Math.min(255, 101 * v));
      img.data[o + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    const t = new CanvasTexture(c);
    t.wrapS = t.wrapT = RepeatWrapping;
    t.colorSpace = SRGBColorSpace;
    return t;
  });
}

/** till-roll paper: warm off-white with fibre and a faint blue guide line */
export function paper(seed = 5): Texture {
  return cached(`paper|${seed}`, () => {
    const S = 128;
    const { c, g } = canvas(S, S);
    g.fillStyle = css(PAPIER);
    g.fillRect(0, 0, S, S);
    const r = rng(seed);
    g.globalAlpha = 0.07;
    for (let i = 0; i < 400; i++) {
      g.fillStyle = i % 3 ? css(INK) : css(CREME.shade);
      g.fillRect(r() * S, r() * S, 1 + r() * 2, 1);
    }
    g.globalAlpha = 1;
    const t = new CanvasTexture(c);
    t.wrapS = t.wrapT = RepeatWrapping;
    t.colorSpace = SRGBColorSpace;
    return t;
  });
}

/** dark backing for cabinet mouths and glass interiors */
export function voidTex(): Texture {
  return cached('void', () => {
    const { c, g } = canvas(4, 4);
    g.fillStyle = css(NACHT.void);
    g.fillRect(0, 0, 4, 4);
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  });
}

// ---------------------------------------------------------------- fonts

let fontsReady: Promise<void> | null = null;
let fontBase = '/fonts/';

/** host apps that serve the kit's assets elsewhere call this before mount() */
export function setFontBase(base: string): void {
  fontBase = base.endsWith('/') ? base : base + '/';
}

/** the three sanctioned faces, loaded before any label is baked */
export function loadFonts(): Promise<void> {
  if (fontsReady) return fontsReady;
  const faces: [string, string][] = [
    ['Monoton', fontBase + 'Monoton-Regular.ttf'],
    ['Barlow Condensed', fontBase + 'BarlowCondensed-Regular.ttf'],
    ['Share Tech Mono', fontBase + 'ShareTechMono-Regular.ttf'],
  ];
  const semi: [string, string] = ['Barlow Condensed', fontBase + 'BarlowCondensed-SemiBold.ttf'];
  fontsReady = (async () => {
    const anyDoc = document as unknown as { fonts?: FontFaceSet };
    if (!anyDoc.fonts || typeof FontFace === 'undefined') return;
    const loads = faces.map(async ([family, url]) => {
      const ff = new FontFace(family, `url(${url})`);
      await ff.load();
      anyDoc.fonts!.add(ff);
    });
    const sb = new FontFace(semi[0], `url(${semi[1]})`, { weight: '600' });
    loads.push(sb.load().then((f) => { anyDoc.fonts!.add(f); }));
    await Promise.all(loads);
    await anyDoc.fonts!.ready;
  })();
  return fontsReady;
}
